import { formattedNumber } from "@/react/helpers/dateTimeHelpers/formattedNumber";
import { SeeMoreLess } from "@circle-react-shared/uikit/SeeMoreLess";
import { FIELD_TYPES } from "../../constants";
import type { NumberOptions } from "../CustomField/NumberInput";

interface CommunityMemberProfileField {
  community_member_choices?: {
    profile_field_choice: {
      value: string;
    };
  }[];
  text?: string;
  textarea?: string;
}

interface GetTextDisplayProps {
  community_member_profile_field: CommunityMemberProfileField;
}

const getTextDisplay = ({
  community_member_profile_field: communityMemberProfileField,
}: GetTextDisplayProps) => {
  const { text } = communityMemberProfileField ?? {};
  if (text?.trim() === "") {
    return null;
  }

  return text;
};

interface NumberDisplayProps {
  community_member_profile_field: CommunityMemberProfileField;
  number_options: NumberOptions;
}

const getNumberDisplay = ({
  community_member_profile_field: communityMemberProfileField,
  number_options,
}: NumberDisplayProps) => {
  const { text } = communityMemberProfileField ?? {};
  if (!text?.trim()) {
    return null;
  }

  return formattedNumber(parseFloat(text), number_options);
};

interface LinkDisplayProps {
  community_member_profile_field: {
    text: string;
  };
}

const getLinkDisplay = ({
  community_member_profile_field: communityMemberProfileField,
}: LinkDisplayProps) => {
  const { text } = communityMemberProfileField ?? {};

  if (!text || text?.trim() === "") {
    return null;
  }
  return (
    <a href={text} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  );
};

interface TextAreaDisplayProps {
  community_member_profile_field: CommunityMemberProfileField;
}

const getTextAreaDisplay = ({
  community_member_profile_field: communityMemberProfileField,
}: TextAreaDisplayProps) => {
  const { textarea } = communityMemberProfileField ?? {};
  const text = getTextDisplay({
    community_member_profile_field: { text: textarea },
  });

  if (!text) {
    return null;
  }

  return (
    <SeeMoreLess lines={8} seeMoreClassName="text-sm">
      {text}
    </SeeMoreLess>
  );
};

interface ChoicesDisplayProps {
  community_member_profile_field: CommunityMemberProfileField;
}

const getChoicesDisplay = ({
  community_member_profile_field: communityMemberProfileField,
}: ChoicesDisplayProps) => {
  // eslint-disable-next-line sonarjs/different-types-comparison -- Disabled for plugin version update. Fix when possible
  if (communityMemberProfileField === null) {
    return null;
  }

  const { community_member_choices: choices = [] } =
    communityMemberProfileField;
  if (!choices.length) {
    return null;
  }
  return choices
    .map(({ profile_field_choice: choice }) => choice.value)
    .join(", ");
};

export const FieldTypeDisplayInformation = {
  [FIELD_TYPES.TEXT]: getTextDisplay,
  [FIELD_TYPES.LINK]: getLinkDisplay,
  [FIELD_TYPES.TEXTAREA]: getTextAreaDisplay,
  [FIELD_TYPES.NUMBER]: getNumberDisplay,
  [FIELD_TYPES.SELECT]: getChoicesDisplay,
  [FIELD_TYPES.CHECKBOX]: getChoicesDisplay,
  [FIELD_TYPES.LOCATION]: getTextDisplay,
};
